import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceInfo, maxQtyFor, computeShipping, comboStep, comboMinQty, freeUnits, isComboOffer } from "@/lib/pricing";
import { api } from "@/lib/api";

const StoreContext = createContext(undefined);
const CART_KEY = "altos_cart_v1";
const VERIFIED_KEY = "altos_verified_v1";

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
}

const snapUp = (product, q) => {
  const step = comboStep(product);
  if (step === 1) return Math.max(0, q);
  return Math.max(comboMinQty(product), Math.ceil(q / step) * step);
};

export function StoreProvider({ children }) {
  const [lines, setLines] = useState(() => load(CART_KEY, []));
  const [altos, setAltos] = useState(() => load(VERIFIED_KEY, { verified: false, altos_id: "", name: "" }));
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(lines));
  }, [lines]);
  useEffect(() => {
    localStorage.setItem(VERIFIED_KEY, JSON.stringify(altos));
  }, [altos]);

  const verified = altos.verified;

  const addItem = (product, qty = 1) => {
    setLines((prev) => {
      const cap = maxQtyFor(product);
      const found = prev.find((l) => l.product.id === product.id);
      const requested = found ? found.quantity + qty : qty;
      const next = Math.min(cap, snapUp(product, requested));
      if (found)
        return prev.map((l) => (l.product.id === product.id ? { ...l, quantity: next } : l));
      return [...prev, { product, quantity: next }];
    });
  };
  const setQuantity = (id, qty) =>
    setLines((prev) => {
      const line = prev.find((l) => l.product.id === id);
      if (!line) return prev;
      const step = comboStep(line.product);
      let q = step > 1 ? Math.round(qty / step) * step : qty;
      if (q <= 0) return prev.filter((l) => l.product.id !== id);
      return prev.map((l) => (l.product.id === id ? { ...l, quantity: Math.min(maxQtyFor(l.product), q) } : l));
    });
  const removeItem = (id) => setLines((prev) => prev.filter((l) => l.product.id !== id));
  const clear = () => setLines([]);

  const loginAltos = (id, name = "") => setAltos({ verified: true, altos_id: id || "", name: name || "" });
  const logoutAltos = () => setAltos({ verified: false, altos_id: "", name: "" });

  const count = useMemo(() => lines.reduce((s, l) => s + l.quantity, 0), [lines]);
  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + getPriceInfo(l.product, verified).unit * l.quantity, 0),
    [lines, verified]
  );
  const totalWeightGrams = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.product.weight_grams) || 0) * (l.quantity + freeUnits(l.product, l.quantity)), 0),
    [lines]
  );
  const totalFreeUnits = useMemo(
    () => lines.reduce((s, l) => s + freeUnits(l.product, l.quantity), 0),
    [lines]
  );
  const totalBV = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.product.bv) || 0) * l.quantity, 0),
    [lines]
  );
  const shipping = computeShipping(settings, totalWeightGrams, subtotal);
  const total = subtotal + shipping;

  const value = {
    lines, count, subtotal, shipping, total, totalWeightGrams, totalBV, totalFreeUnits,
    verified, altosId: altos.altos_id, altosName: altos.name, settings,
    addItem, setQuantity, removeItem, clear, loginAltos, logoutAltos,
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}
